import type { LocaleCode } from '@/pages/cogo-marketing/i18n/locales'

export interface DesignDirectionCopy {
  eyebrow: string
  title: string
  body: string
  selected: string
  directions: {
    calm: { title: string; detail: string }
    bold: { title: string; detail: string }
    compact: { title: string; detail: string }
  }
  fineTune: string
  fineTuneDetail: string
  advanced: string
  advancedDetail: string
}

const COPY: Record<LocaleCode, DesignDirectionCopy> = {
  en: {
    eyebrow: 'Start with the outcome',
    title: 'Choose how the product should feel',
    body: 'Pick a direction first. Studio applies a complete, editable theme and shows the result on real cogo blocks.',
    selected: 'Selected',
    directions: {
      calm: { title: 'Calm and clear', detail: 'Trustworthy blue, readable type, and comfortable spacing.' },
      bold: { title: 'Bold and expressive', detail: 'Violet accents, larger shapes, and a stronger editorial voice.' },
      compact: { title: 'Compact and focused', detail: 'Dense information, restrained green, and efficient controls.' },
    },
    fineTune: 'Preview and fine-tune',
    fineTuneDetail: 'Adjust individual colours, type, charts, and corners only when the overall direction is right.',
    advanced: 'Source details and advanced checks',
    advancedDetail: 'Saved systems, source-derived axes, verified libraries, and project-token guidance.',
  },
  ko: {
    eyebrow: '결과부터 시작',
    title: '제품이 어떤 느낌이어야 하는지 선택하세요',
    body: '방향을 먼저 고르면 Studio가 편집 가능한 전체 테마를 적용하고 실제 cogo 블록에서 결과를 보여 줍니다.',
    selected: '선택됨',
    directions: {
      calm: { title: '차분하고 명확하게', detail: '신뢰감 있는 파란색, 읽기 쉬운 글자, 편안한 간격을 사용합니다.' },
      bold: { title: '대담하고 인상적으로', detail: '보라색 강조, 큰 형태, 선명한 편집 톤을 사용합니다.' },
      compact: { title: '간결하고 집중되게', detail: '정보 밀도를 높이고 절제된 초록색과 효율적인 컨트롤을 사용합니다.' },
    },
    fineTune: '미리 보고 세부 조정',
    fineTuneDetail: '전체 방향이 맞을 때 색상, 글자, 차트, 모서리만 세부 조정하세요.',
    advanced: '소스 세부 정보와 고급 점검',
    advancedDetail: '저장된 시스템, 소스에서 읽은 축, 확인된 라이브러리, 프로젝트 토큰 안내를 봅니다.',
  },
  ja: {
    eyebrow: '成果から始める',
    title: 'プロダクトの印象を選んでください',
    body: '方向を先に選ぶと、Studio が編集可能なテーマ全体を適用し、実際の cogo ブロックで結果を表示します。',
    selected: '選択済み',
    directions: {
      calm: { title: '落ち着いて明快', detail: '信頼感のある青、読みやすい書体、ゆとりのある余白。' },
      bold: { title: '大胆で印象的', detail: '紫のアクセント、大きな形、強い編集トーン。' },
      compact: { title: 'コンパクトで集中', detail: '高い情報密度、控えめな緑、効率的な操作。' },
    },
    fineTune: 'プレビューして微調整',
    fineTuneDetail: '全体の方向が合った後に、色、書体、チャート、角を調整します。',
    advanced: 'ソース詳細と高度なチェック',
    advancedDetail: '保存済みシステム、ソース由来の軸、確認済みライブラリ、プロジェクトトークン。',
  },
  zh: {
    eyebrow: '从结果开始',
    title: '选择产品应呈现的感觉',
    body: '先选择方向，Studio 会应用完整且可编辑的主题，并在真实 cogo 区块中显示结果。',
    selected: '已选择',
    directions: {
      calm: { title: '沉稳清晰', detail: '可信的蓝色、易读字体和舒适间距。' },
      bold: { title: '大胆鲜明', detail: '紫色强调、更大的形状和更强的编辑感。' },
      compact: { title: '紧凑专注', detail: '更高信息密度、克制的绿色和高效控件。' },
    },
    fineTune: '预览并微调',
    fineTuneDetail: '整体方向确定后，再调整颜色、字体、图表和圆角。',
    advanced: '来源详情与高级检查',
    advancedDetail: '查看已保存系统、来源推导轴、已验证库和项目令牌说明。',
  },
  'zh-TW': {
    eyebrow: '從結果開始',
    title: '選擇產品應呈現的感覺',
    body: '先選擇方向，Studio 會套用完整且可編輯的主題，並在真實 cogo 區塊中顯示結果。',
    selected: '已選擇',
    directions: {
      calm: { title: '沉穩清晰', detail: '可信的藍色、易讀字體和舒適間距。' },
      bold: { title: '大膽鮮明', detail: '紫色強調、更大的形狀和更強的編輯感。' },
      compact: { title: '緊湊專注', detail: '更高資訊密度、克制的綠色和高效控制項。' },
    },
    fineTune: '預覽並微調',
    fineTuneDetail: '整體方向確定後，再調整顏色、字體、圖表和圓角。',
    advanced: '來源詳情與進階檢查',
    advancedDetail: '查看已儲存系統、來源推導軸、已驗證資料庫和專案權杖說明。',
  },
}

export function getDesignDirectionCopy(locale: LocaleCode): DesignDirectionCopy {
  return COPY[locale] ?? COPY.en
}
